// My Schedule (nursing-ops-2 #01): the logged-in nurse's own upcoming shifts,
// resolved SERVER-side via mine=1/current_actor — this view never knows or
// sends a nurse id. Also hosts the nurse-side leave UI (#02): apply for leave
// and track my requests, same server-resolved identity.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Chip, LinearProgress, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from "@mui/material";

import { NURSE_COLORS } from "../theme";
import { getMyRoster, listOf } from "../api/nurseApi";
import MyLeaveSection from "../components/MyLeaveSection";

const SHIFT_LABEL = { morning: "Morning", evening: "Evening", night: "Night" };
const STATUS_LABEL = { on_duty: "On Duty", overtime: "Overtime", leave: "Leave", na: "N/A" };
const STATUS_CHIP_COLOR = { on_duty: "success", overtime: "warning", leave: "default", na: "default" };

const toStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function MySchedule() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Two weeks back through six weeks ahead — enough context without paging.
  const range = useMemo(() => {
    const from = new Date();
    from.setDate(from.getDate() - 14);
    const to = new Date();
    to.setDate(to.getDate() + 42);
    return { from_date: toStr(from), to_date: toStr(to) };
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    getMyRoster(range)
      .then((r) => setRows(listOf(r)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { reload(); }, [reload]);

  const todayStr = toStr(new Date());
  const sorted = useMemo(
    () => [...rows].sort((a, b) => String(a.roster_date).localeCompare(String(b.roster_date))),
    [rows]
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">My Schedule</Typography>
        <Typography variant="body2" color="text.secondary">
          Your rostered shifts, and your leave requests below.
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto", mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Date</TableCell><TableCell>Department</TableCell><TableCell>Ward</TableCell><TableCell>Shift</TableCell><TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((r) => (
              <TableRow key={r.id} hover selected={r.roster_date === todayStr}>
                <TableCell>
                  {r.roster_date}
                  {r.roster_date === todayStr && <Chip size="small" color="info" label="Today" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{r.department_id || "—"}</TableCell>
                <TableCell>{r.ward_code}</TableCell>
                <TableCell>{SHIFT_LABEL[r.shift] || r.shift}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={STATUS_LABEL[r.status] || r.status}
                    color={STATUS_CHIP_COLOR[r.status] || "default"}
                    variant={r.status === "leave" || r.status === "na" ? "outlined" : "filled"}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!loading && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No shifts rostered for you in this window.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <MyLeaveSection onChanged={reload} />
    </Box>
  );
}
